import React, { useState } from "react";
import { useNavigate } from 'react-router-dom'; // 👈 IMPORT useNavigate
import { Menu, X, Search, User, ShoppingCart } from "lucide-react"; 

// 1. Import your logo image (Update the path as necessary for your project structure)
import logoImage from '../assets/logo_dark.webp'; // <-- replace with your Leatherhood logo path

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate(); // 👈 INITIALIZE useNavigate

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "Shop", href: "/shop" },
    { name: "About", href: "/about" },
    { name: "Contact", href: "/contact" },
  ];

  // --- Logic: Derive active link from the current URL path ---
  // NOTE: In a real React Router app, you'd typically use useLocation().pathname instead of window.location.pathname
  const currentPath = window.location.pathname;

  const getActiveLinkName = (path) => {
    const normalizedPath = path.toLowerCase().replace(/\/$/, '');
    
    const foundLink = navLinks.find(link => {
      const normalizedHref = link.href === '/' ? '' : link.href.toLowerCase();
      
      if (link.href === '/') {
        return normalizedPath === '' || normalizedPath === '/';
      }
      
      return normalizedPath.startsWith(normalizedHref);
    });

    return foundLink ? foundLink.name : 'Home';
  };

  const activeLinkName = getActiveLinkName(currentPath);

  // --- End of Logic ---

  return (
    <nav className="fixed w-full z-50">

      {/* Main Navbar Content */}
      <div className="bg-offwhite shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left Section: Nav Links (Desktop) / Mobile Menu Button (Mobile) */}
            <div className="flex items-center">
              {/* Mobile Menu Button (visible on small screens) */}
              <div className="md:hidden flex items-center mr-4">
                <button onClick={() => setIsOpen(!isOpen)} aria-label={isOpen ? "Close menu" : "Open menu"}>
                  {isOpen ? <X className="w-6 h-6 text-primary" /> : <Menu className="w-6 h-6 text-primary" />}
                </button>
              </div>

              {/* Desktop Nav Links (hidden on small screens) */}
              <div className="hidden md:flex space-x-6 h-full items-center">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    className={`
                      text-darktaupe hover:text-tan transition-colors font-medium h-full flex items-center
                      ${
                        activeLinkName === link.name 
                          ? 'border-b-2 border-primary text-primary' 
                          : ''
                      }
                    `}
                  >
                    {link.name}
                  </a>
                ))}
              </div>
            </div>

            {/* Center Section: Logo */}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <a href="/">
                <img 
                  src={logoImage}
                  alt="LeatherHood Logo" 
                  className="h-10 w-auto"
                /> 
              </a>
            </div>

            {/* Right Section: Search, Profile, Cart */}
            <div className="flex items-center space-x-4">
            
              <button 
                onClick={() => navigate('/login')} // Use navigate for smooth routing
                aria-label="Profile or Login"
              >
                <User className="w-6 h-6 text-taupe cursor-pointer hover:text-primary transition-colors" />
              </button>
              
              <a href="/cart">
                <ShoppingCart className="w-6 h-6 text-taupe cursor-pointer hover:text-primary transition-colors" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Dropdown */}
      {isOpen && (
        <div className="md:hidden bg-offwhite px-4 pb-4 space-y-3 border-t border-gray-100">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={() => { setIsOpen(false); }}
              className={`
                block py-2 font-medium 
                ${
                  activeLinkName === link.name 
                    ? 'bg-beige text-primary rounded-md' 
                    : 'text-darktaupe hover:text-tan'
                }
              `}
            >
              {link.name}
            </a>
          ))}
          <div className="pt-2 space-y-2 border-t border-gray-100">
            
            
            {/* 👈 CHANGE APPLIED HERE: Mobile Profile Button */}
            <button 
              onClick={() => { setIsOpen(false); navigate('/login'); }}
              className="w-full border border-taupe text-taupe px-4 py-2 rounded-md hover:bg-taupe hover:text-offwhite transition-colors flex items-center justify-center space-x-2"
            >
              <User className="w-5 h-5" /> <span>Profile</span>
            </button>
            
            <button className="w-full bg-tan text-white px-4 py-2 rounded-md hover:bg-primary transition-colors flex items-center justify-center space-x-2">
              <ShoppingCart className="w-5 h-5" /> <span>Cart</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;